/**
 * Sound hooks (design system, FOUNDATION — v3 §5 "sound-ready").
 *
 * MUTE BY DEFAULT, forever, until the user flips the toggle. No autoplay, no
 * assets shipped yet — this is the clean seam the game layer plugs samples
 * into later (swipe-commit, stamp, reveal sting, killstreak).
 *
 *   playSting("reveal_reject");        // no-op unless enabled by the user
 *   const on = useSoundEnabled();      // settings toggle state
 *   setSoundEnabled(true);             // the ONLY way sound turns on
 *
 * Rules (DESIGN-BRIEF v3 §5):
 *  - never autoplay: playSting is inert until setSoundEnabled(true) was called
 *    by a real user action;
 *  - prefers-reduced-motion mutes everything regardless of the toggle;
 *  - samples must be tiny, self-hosted (CSP 'self'), registered via
 *    registerSting() by the layer that lazy-loads them.
 */
import { useSyncExternalStore } from "react";
import { prefersReducedMotion } from "./motion.js";

export type StingName =
  | "swipe_commit"
  | "stamp"
  | "reveal_classic"
  | "reveal_reject"
  | "killstreak"
  | "shield_armed";

const STORAGE_KEY = "humble.sound";

let enabled =
  typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY) === "on";
const listeners = new Set<() => void>();
const samples = new Map<StingName, HTMLAudioElement>();

function notify() {
  for (const l of listeners) l();
}

/** User-driven toggle — the only way audio ever becomes audible. */
export function setSoundEnabled(on: boolean): void {
  enabled = on;
  try {
    localStorage.setItem(STORAGE_KEY, on ? "on" : "off");
  } catch {
    /* private mode — session-only toggle */
  }
  notify();
}

export function isSoundEnabled(): boolean {
  return enabled;
}

/** Reactive toggle state for the settings surface. */
export function useSoundEnabled(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    isSoundEnabled,
    () => false,
  );
}

/**
 * Register a self-hosted sample for a sting (lazy — call it from the layer
 * that owns the moment, e.g. the reveal, when it first mounts).
 */
export function registerSting(name: StingName, url: string): void {
  if (typeof Audio === "undefined" || samples.has(name)) return;
  const el = new Audio(url);
  el.preload = "none";
  el.volume = 0.5;
  samples.set(name, el);
}

/**
 * Fire-and-forget sting. Inert unless the user enabled sound, a sample is
 * registered, and reduced motion is off. Failures are swallowed — a missed
 * sting is no loss.
 */
export function playSting(name: StingName): void {
  if (!enabled || prefersReducedMotion()) return;
  const el = samples.get(name);
  if (!el) return;
  el.currentTime = 0;
  void el.play().catch(() => {
    /* browser veto (no gesture yet) — stay silent */
  });
}
